import {
  ConfigurableChartAction,
  configurableChartReducer,
  DataExplorationChart,
  DataExplorationSeries
} from "reducers/configurableChartReducer";
import React, { useCallback, useContext, useState } from "react";
import { v4 as createUUID } from "uuid";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { usePostChart } from "hooks/charts/usePostChart";
import { MoreActionButton } from "components/DataExplorationV2/MoreActionButton";
import { ConfigurableChart } from "components/DataExplorationV2/ConfigurableChart";
import { AddSectionBar } from "components/DataExplorationV2/AddSectionBar";
import { SaveChartDialog } from "components/DataExplorationV2/SaveChartDialog";
import { styled } from "@mui/material/styles";
import { CHART_LIMIT, generateNewChart } from "core/data-exploration-v2-utils";
import { SavedChart } from "interfaces/chart";
import { useGetChartsList } from "hooks/charts/useGetChartsList";
import { useLazyGetChartById } from "hooks/charts/useLazyGetChartById";
import Skeleton from "@mui/material/Skeleton";
import { LoadChartDialog } from "components/DataExplorationV2/LoadChartDialog";
import Tooltip from "@mui/material/Tooltip";

interface ChartEntry {
  entryId: string;
  loading: boolean;
  chart: DataExplorationChart;
}

/** Creates a new chart entry that can be added to the chart list */
const createNewChartEntry = (
  entryId: string | undefined,
  loading: boolean = false
): ChartEntry => {
  return {
    entryId: entryId ?? createUUID(),
    loading,
    chart: generateNewChart()
  };
};

/**
 * Gets all the valid series in a chart
 * @param chart The chart to get the series from
 * @returns an array of series
 */
const getValidSeries = (
  chart: DataExplorationChart
): DataExplorationSeries[] => {
  return chart.series.filter(
    x =>
      (x.dataSource.type === "scada-datetime-tag" &&
        x.dataSource.args.tagId !== null &&
        x.dataSource.args.machineId !== null) ||
      x.dataSource.type !== "scada-datetime-tag"
  );
};

/**
 * Checks if the chart passed in is valid. A Chart is invalid if it has no valid series.
 * @param chart the chart to check
 */
const isChartValid = (chart: DataExplorationChart): boolean => {
  return getValidSeries(chart).length > 0;
};

/**
 * Takes a data exploration chart and filters series, y-axes and plot lines which aren't valid.
 * @param chart the chart object to map to valid chart schema
 */
const mapChartToValidSchema = (
  chart: DataExplorationChart
): DataExplorationChart => {
  return {
    version: chart.version,
    title: chart.title,
    xAxes: chart.xAxes,
    yAxes: chart.yAxes.filter(x => x.tagId !== null),
    plotLines: chart.plotLines.filter(
      x => x.targetAxis !== null && x.value !== null
    ),
    series: getValidSeries(chart)
  };
};

/**
 * A hook for managing a list of charts, provides functions needed to create, update and delete the charts.
 * @param initialChart the initial chart which should be displayed in the list.
 */
const useChartList = (
  initialChart: DataExplorationChart | null
): {
  chartEntries: ChartEntry[];
  addBlankChart: (entryId?: string, loading?: boolean) => void;
  updateChartById: (entryId: string, action: ConfigurableChartAction) => void;
  updateLoadingChartEntry: (
    entryId: string,
    chart: DataExplorationChart
  ) => void;
  deleteChartById: (entryId: string) => void;
} => {
  const [chartEntries, setChartEntries] = useState<ChartEntry[]>(() =>
    initialChart
      ? [{ entryId: createUUID(), loading: false, chart: initialChart }]
      : []
  );

  /** Adds a blank chart if the chart limit has not been reached */
  const addBlankChart = useCallback(
    (entryId: string | undefined, loading: boolean = false): void => {
      setChartEntries(prevState => {
        if (prevState.length < CHART_LIMIT) {
          return [...prevState, createNewChartEntry(entryId, loading)];
        } else {
          return prevState;
        }
      });
    },
    []
  );

  /** Updates the chart of the specific entry using the reducer function */
  const updateChartById = useCallback(
    (entryId: string, action: ConfigurableChartAction) => {
      setChartEntries(prevCharts => {
        const index = prevCharts.findIndex(x => x.entryId === entryId);
        const prevChartEntry = prevCharts[index];
        const newChartState = configurableChartReducer(
          prevChartEntry.chart,
          action
        );
        const newChartEntries = [...prevCharts];
        newChartEntries.splice(index, 1, {
          ...prevChartEntry,
          chart: newChartState
        });
        return newChartEntries;
      });
    },
    []
  );

  /** Updates a chart entry which was loading with the result of the load */
  const updateLoadingChartEntry = useCallback(
    (entryId: string, chart: DataExplorationChart) => {
      setChartEntries(prevState => {
        const entryIndex = prevState.findIndex(x => x.entryId === entryId);
        const newEntries = [...prevState];
        const newEntry: ChartEntry = {
          entryId,
          loading: false,
          chart
        };
        if (entryIndex !== -1) {
          newEntries.splice(entryIndex, 1, newEntry);
        } else {
          newEntries.push({
            entryId,
            loading: false,
            chart
          });
        }
        return newEntries;
      });
    },
    []
  );

  /** removes the specific chart entry from the list */
  const deleteChartById = useCallback((entryId: string) => {
    setChartEntries(prevChartEntries => {
      const index = prevChartEntries.findIndex(x => x.entryId === entryId);
      const newChartEntries = [...prevChartEntries];
      newChartEntries.splice(index, 1);
      return newChartEntries;
    });
  }, []);

  return {
    chartEntries,
    addBlankChart,
    updateChartById,
    updateLoadingChartEntry,
    deleteChartById
  };
};

interface ChartListProps {
  initialChart: DataExplorationChart | null;
}

export const ChartList = ({ initialChart }: ChartListProps): JSX.Element => {
  const {
    chartEntries,
    addBlankChart,
    updateChartById,
    updateLoadingChartEntry,
    deleteChartById
  } = useChartList(initialChart);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { loading: saveLoading, postChartCallback } = usePostChart();
  const [chartToSave, setChartToSave] = useState<DataExplorationChart>();
  const [chartSaveError, setChartSaveError] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [loadChartDialog, setLoadChartDialog] = useState<boolean>(false);
  const {
    loading: savedChartsLoading,
    chartsList: savedCharts,
    refetchChartsList: refetchSavedCharts
  } = useGetChartsList();

  const { fetchChart: getSavedChartById } = useLazyGetChartById();

  const handleSaveChart = (
    chartName: string,
    chart: DataExplorationChart
  ): void => {
    setChartSaveError(false);
    postChartCallback({
      chart_name: chartName,
      chart_object: mapChartToValidSchema(chart)
    }).then(
      _resolve => {
        addSnackbarToQueue({
          message: `${chartName} chart was saved.`,
          severity: "success"
        });
        setSaveDialogOpen(false);
        refetchSavedCharts();
      },
      _reject => {
        addSnackbarToQueue({
          message: "Failed to save the chart.",
          severity: "error"
        });
        setChartSaveError(true);
      }
    );
  };

  /** Load chart list by saved chart id */
  const handleLoadChart = (chartId: number | undefined): void => {
    if (chartId !== undefined) {
      setLoadChartDialog(false);
      const entryId = createUUID();

      addBlankChart(entryId, true);
      getSavedChartById(chartId)
        .then((response: SavedChart) => {
          if (response?.chart_object) {
            updateLoadingChartEntry(
              entryId,
              response.chart_object as DataExplorationChart
            );
          }
        })
        .catch((_error: any) => {
          addSnackbarToQueue({
            message: "Failed to load the chart.",
            severity: "error"
          });
        });
    }
  };

  return (
    <>
      <ContentWrapper>
        {chartEntries.map(({ entryId, loading, chart }) => (
          <div key={entryId}>
            {loading ? (
              <>
                <Skeleton variant="text" height={20} />
                <SkeletonMenuWrapper>
                  <Skeleton variant="rectangular" width={100} height={30} />
                </SkeletonMenuWrapper>
                <Skeleton variant="rectangular" height={400} />
              </>
            ) : (
              <>
                <Divider />
                <MenuWrapper>
                  <MoreActionButton
                    saveDisabled={!isChartValid(chart)}
                    onSaveChart={() => {
                      setChartToSave(chart);
                      setSaveDialogOpen(true);
                    }}
                    onDeleteChart={() => deleteChartById(entryId)}
                  />
                </MenuWrapper>
                <ConfigurableChart
                  chart={chart}
                  dispatchChartUpdate={action =>
                    updateChartById(entryId, action)
                  }
                />
              </>
            )}
          </div>
        ))}
        <ButtonWrapper>
          <Tooltip
            title={`Cannot add more than ${CHART_LIMIT} charts.`}
            arrow
            disableHoverListener={chartEntries.length < CHART_LIMIT}
          >
            <span>
              <AddSectionBar
                disabled={chartEntries.length >= CHART_LIMIT}
                onClickLoadChart={() => setLoadChartDialog(true)}
                onClickAddChart={() => addBlankChart()}
              />
            </span>
          </Tooltip>
        </ButtonWrapper>
      </ContentWrapper>
      {saveDialogOpen && (
        <SaveChartDialog
          open={saveDialogOpen}
          loading={saveLoading}
          savedCharts={savedCharts}
          initialName={chartToSave?.title}
          error={chartSaveError}
          onClose={() => {
            setSaveDialogOpen(false);
            setChartSaveError(false);
          }}
          onSave={chartName =>
            chartToSave && handleSaveChart(chartName, chartToSave)
          }
        />
      )}
      {loadChartDialog && (
        <LoadChartDialog
          open={loadChartDialog}
          onClose={() => setLoadChartDialog(false)}
          loading={savedChartsLoading}
          savedCharts={savedCharts}
          onLoad={handleLoadChart}
          onDeleteChart={refetchSavedCharts}
        />
      )}
    </>
  );
};

const ContentWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(1)};
  padding-top: 0;
`;

const Divider = styled("div")`
  width: 100%;
  border: 1px solid #d9d9d9;
  margin: ${props => props.theme.spacing(1)} 0 0;
`;

const MenuWrapper = styled("div")`
  text-align: right;
  padding: 10px 50px;
`;

const ButtonWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SkeletonMenuWrapper = styled("div")`
  width: 100%;
  display: flex;
  justify-content: end;
  padding: ${props => props.theme.spacing(1)} 0;
`;
